import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { confirmEmail } from '../services/resortAdminApi';
import { CheckCircle, XCircle } from 'lucide-react';

export const ConfirmEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Confirm email on mount
  useEffect(() => {
    const confirmEmailToken = async () => {
      if (!token) {
        setError('No token provided');
        setLoading(false);
        return;
      }
      
      setConfirming(true);
      try {
        const result = await confirmEmail(token);
        if (result.status === 'success') {
          setSuccess(true);
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setError(result.message || 'Failed to confirm email');
        }
      } catch (error: any) {
        setError(error.message || 'Failed to confirm email. Token may be invalid or expired.');
      } finally {
        setLoading(false);
        setConfirming(false);
      }
    };
    
    confirmEmailToken();
  }, [token, navigate]);

  if (loading || confirming) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-alps-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Confirming email...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">Email Confirmed</h1>
            <p className="text-gray-600 mb-4">Your email address has been confirmed successfully. You will be redirected to the login page.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md w-full">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Confirmation Failed</h1>
          <p className="text-gray-600 mb-4">{error || 'This email confirmation link is invalid or has expired.'}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-alps-primary text-white rounded-md hover:bg-alps-primary-dark"
          >
            Go to Login
          </button>
        </div>
      </Card>
    </div>
  );
};

